'use client';

import type { FC } from 'react';
import { useDisplayUserName } from '../../hooks/useDisplayUserName';
import { extractSkinName } from '../../utils/formatters';
import { rarityGradient } from '../../utils/rarityGradient';
import { Credits } from '../Credits';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { SkinImage } from '../ui/SkinImage';
import type { RainHistoryWinner } from './types';

export interface RainWinnerRowProps {
  winner: RainHistoryWinner;
  onClick?: () => void;
}

export const RainWinnerRow: FC<RainWinnerRowProps> = ({ winner, onClick }) => {
  const displayUserName = useDisplayUserName();

  const content = (
    <>
      <UserAvatar
        userId={winner.userId}
        firstName={winner.firstName}
        lastName={winner.lastName}
        level={winner.level}
        size="m"
      />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-sm font-semibold text-label-primary">
          {displayUserName(winner.firstName, winner.lastName)}
        </div>

        <div className="mt-0.5 truncate text-sm text-label-secondary">{extractSkinName(winner.item.name)}</div>
        <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-label-tertiary">
          <Credits amount={winner.item.priceInCredits} iconSize={11} />
        </div>
      </div>
      <div
        className="relative size-16 shrink-0 overflow-hidden rounded-xl"
        style={{ background: rarityGradient(winner.item.rarityColor) }}
      >
        <SkinImage
          name={winner.item.hashName}
          src={winner.item.imageUrl}
          alt={winner.item.name}
          className="absolute inset-0"
          imgClassName="h-full w-full object-contain p-0.5"
        />
      </div>
    </>
  );

  const box = 'flex w-full items-center gap-3 border-t border-white/5 px-3.5 py-3 text-start';

  if (!onClick) return <div className={box}>{content}</div>;
  return (
    <button type="button" onClick={onClick} className={`${box} transition active:opacity-80`}>
      {content}
    </button>
  );
};
